import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { useShipmentService } from "@/Services/ShipmentService";
import { bookShipmentGetPreviousInFlow } from "../Helpers/bookShipmentGetPreviousInFlow";
import type { useBookShipmentFinishBookingFlow } from "./useBookShipmentFinishBookingFlow";
import type { BookShipmentShipmentType } from "@/Shared/Types/bookShipmentSliceTypes";
import { loadFromShipmentToModify } from "@/Slices/bookShipmentSlice";

interface FlowNavigationParams {
  shipment: BookShipmentShipmentType;
  savedLocations: Array<SavedLocation>;
  shipmentId: string | undefined;
  isCurrentScreenDirty: boolean;
  finishBookingFlow: ReturnType<typeof useBookShipmentFinishBookingFlow>;
}

/**
 * Moving between the steps of the booking flow, and the two things we stop to
 * ask about first.
 *
 * Going back to "details" is not navigation, it is a modify: the shipment has
 * already been quoted, and changing it means those quotes no longer describe
 * it. So the quoted shipment is deleted and the customer starts from the
 * details screen again — which is worth confirming, because the quotes go.
 *
 * The other stop is a screen with unsaved input. Leaving loses it, so the
 * target is parked until the customer says it is fine.
 *
 * The dialogs come back as a value for the caller to render unconditionally,
 * above its loading and error guards. Rendering them below would let a
 * shipment load that finishes mid-conversation unmount an open dialog.
 */
export function useBookShipmentFlowNavigation(params: FlowNavigationParams) {
  const { shipment, savedLocations, shipmentId, isCurrentScreenDirty } = params;
  const { finishBookingFlow } = params;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const shipmentsService = useShipmentService();

  const [modifyConfirmOpen, setModifyConfirmOpen] = useState(false);
  // The flow identifier we'll navigate to once the customer confirms losing
  // this screen's unsaved input.
  const [dirtyConfirmTarget, setDirtyConfirmTarget] = useState<
    string | undefined
  >();

  function onModify() {
    setModifyConfirmOpen(true);
  }

  async function completeModify() {
    const shipmentIdToDelete = shipmentId;
    // Modify is only reachable from flows with a quoted shipment in the url
    if (shipmentIdToDelete === undefined) {
      return;
    }
    dispatch(loadFromShipmentToModify({ shipment, savedLocations }));
    await shipmentsService.deleteQuotedShipment(
      shipmentIdToDelete,
      "Modified for new quotes",
    );
    navigate("/book/details");
  }

  async function onPrevious(flowIdentifier: string) {
    const previousIdentifier = bookShipmentGetPreviousInFlow(
      finishBookingFlow,
      flowIdentifier,
    );
    await onNavigate(flowIdentifier, previousIdentifier);
  }

  async function onNavigate(
    _currentFlowIdentifier: string,
    proposedFlowIdentifier: string,
  ) {
    /*
            The flow is
                details
                quotes
                pickup-address
                delivery-address
                broker
                payment
                review
        */

    if (proposedFlowIdentifier === "details") {
      onModify();
      return;
    }

    function doNavigation() {
      const newUrl =
        "/book/" + proposedFlowIdentifier + "?shipmentId=" + shipmentId;
      navigate(newUrl);
    }

    if (isCurrentScreenDirty) {
      setDirtyConfirmTarget(proposedFlowIdentifier);
    } else {
      doNavigation();
    }
  }

  // Declared above the early returns below so the dialogs survive them: the
  // shipment load can flip the loading/error guards while a confirm dialog is
  // open, and an early return without the dialogs would silently unmount it
  // (a screen that owns an open Dialog must never unmount it).
  const confirmDialogs = (
    <>
      <Dialog
        open={modifyConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setModifyConfirmOpen(false);
        }}
        title="Confirm modify"
        description="Modifying this requires getting new quotes from carriers. The previous quotes will be discarded. Is this ok?"
        okText="Get New Quotes"
        onOk={() => {
          setModifyConfirmOpen(false);
          completeModify();
        }}
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={dirtyConfirmTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setDirtyConfirmTarget(undefined);
        }}
        title="Confirm"
        description="Navigating away will lose any information you entered on this screen. Is this ok?"
        okText="Discard and Leave"
        onOk={() => {
          const target = dirtyConfirmTarget;
          setDirtyConfirmTarget(undefined);
          if (target !== undefined) {
            navigate("/book/" + target + "?shipmentId=" + shipmentId);
          }
        }}
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return { onModify, onPrevious, onNavigate, dialogs: confirmDialogs };
}
